import { useContext } from "react";
import { FormContextMA } from "../form-context";
import {
  CheckInput,
  DataField,
  Divider,
  H2,
  H3,
  P,
  Section,
  TealSubSection,
} from "./common-components";
import {
  allergensEnum,
  nasalCongestionEnum,
  nasalSymptomsEnum,
  otherSymptomsEnum,
  redFlagSymptomsEnum,
} from "../../patient/clinical-services/service-steps/signs-and-symptoms/data";
import { capitalizeFirstLetter } from "../../../helpers/functions";
import { GetAlertConfirmation } from "../../../helpers/constant";
import { GetMultiDropDownValue } from "./data";

export const Symptoms = ({formType, clinicalServiceName, preAssessment, signsAndSymptoms}) => {
  // const { clinicalServiceName, preAssessment, signsAndSymptoms } =
  //   useContext(FormContextMA);

  const minorAilmentName =
    { allergicRhinitis: "Allergic Rhinitis" }[clinicalServiceName] || "";

  const hasHistory = !!preAssessment?.previousHistoryIndicator;

  const selectedRedFlagValues = Array.isArray(signsAndSymptoms?.redFlagSymptoms)
    ? signsAndSymptoms.redFlagSymptoms.filter((v) => v !== "none")
    : [];
  const uniqueRedFlagValues = Array.from(new Set(selectedRedFlagValues));
  const redFlagSymptoms =
    uniqueRedFlagValues.length > 0
      ? GetMultiDropDownValue(
          redFlagSymptomsEnum.options,
          uniqueRedFlagValues.join(", ")
        )
      : null;
  const hasRedFlag = uniqueRedFlagValues.length > 0;

  const eyeSymptoms = Array.isArray(signsAndSymptoms?.otherSymptoms) 
    ? signsAndSymptoms.otherSymptoms.filter(
        (x) => x === "wateryEyes" || x === "itchyEyes"
      )
    : [];

  const typicalSymptoms = {
    nasal:
      signsAndSymptoms?.nasalSymptoms?.length &&
      !signsAndSymptoms?.nasalSymptoms?.includes("none")
        ? GetMultiDropDownValue(
            nasalSymptomsEnum.options,
            Array.isArray(signsAndSymptoms?.nasalSymptoms) ? signsAndSymptoms.nasalSymptoms.join(", ") : ""
          )
        : null,
    rashHives:
      !!signsAndSymptoms?.otherSymptoms?.length &&
      !signsAndSymptoms?.otherSymptoms?.includes("none") &&
      !!signsAndSymptoms?.otherSymptoms?.includes?.("skinRash"),
    congestion:
      signsAndSymptoms?.nasalCongestion &&
      signsAndSymptoms?.nasalCongestion !== "noCongestion"
        ? nasalCongestionEnum.options.find(
            (n) => n.value === signsAndSymptoms?.nasalCongestion
          )?.header
        : null,
    eye: GetMultiDropDownValue(
      otherSymptomsEnum.options,
      eyeSymptoms.join(", ")
    ),
    otherAR:
      !!signsAndSymptoms?.otherSymptoms?.length &&
      !signsAndSymptoms?.otherSymptoms?.includes("none")
        ? GetMultiDropDownValue(
            otherSymptomsEnum.options,
            Array.isArray(signsAndSymptoms?.otherSymptoms) ? signsAndSymptoms.otherSymptoms.join(", ") : ""
          )
        : null,
  };

  return (
    <Section formType={formType}>
      <H2>Review of Symptoms</H2>
      <div className="space-y-1">
        <H3>Previous History of {minorAilmentName}</H3>
        <DataField
          label={
            hasHistory
              ? "Patient has a history or recent diagnosis of selected Minor Ailment"
              : "Patient DOES NOT have a history or recent diagnosis of selected Minor Ailment"
          }
        />
      </div>
      <Divider />
      <div className="space-y-1">
        <H3>Presenting Symptoms</H3>
        <DataField
          label={capitalizeFirstLetter(preAssessment?.presentingSymptoms || "")}
        />
      </div>
      <Divider />
      <div className="space-y-2">
        <H3>Typical Signs & Symptoms</H3>
        <div className="grid grid-cols-2 gap-4">
          <CheckInput
            checked={!!typicalSymptoms.nasal}
            label={
              <>
                Nasal Symptoms
                {typicalSymptoms.nasal ? (
                  <>
                    <br />
                    {typicalSymptoms.nasal}
                  </>
                ) : null}
              </>
            }
          />
          <CheckInput
            checked={!!typicalSymptoms.rashHives}
            label={
              <>
                Rash / Hives
                {typicalSymptoms.rashHives ? (
                  <>
                    <br />
                    {typicalSymptoms.rashHives}
                  </>
                ) : null}
              </>
            }
          />
          <CheckInput
            checked={!!typicalSymptoms.congestion}
            label={
              <>
                Nasal Congestion
                {typicalSymptoms.congestion ? (
                  <>
                    <br />
                    {typicalSymptoms.congestion}
                  </>
                ) : null}
              </>
            }
          />
          <CheckInput
            checked={Object.values(typicalSymptoms).every((v) => !v)}
            label={<>No Typical Signs & Symptoms</>}
          />
          <CheckInput
            checked={!!typicalSymptoms.eye}
            label={
              <>
                Eye Symptoms
                {typicalSymptoms.eye ? (
                  <>
                    <br />
                    {typicalSymptoms.eye}
                  </>
                ) : null}
              </>
            }
          />
          <CheckInput
            checked={!typicalSymptoms.nasal && !typicalSymptoms.congestion}
            label={<>No Nasal Symptoms</>}
          />
          <CheckInput
            checked={!!typicalSymptoms.otherAR}
            label={
              <>
                Other Allergic Rhinitis Symptoms
                {typicalSymptoms.otherAR ? (
                  <>
                    <br />
                    {typicalSymptoms.otherAR}
                  </>
                ) : null}
              </>
            }
          />
        </div>
        {signsAndSymptoms?.typicalConfirmationType !== 0 && (
          <TealSubSection className="space-y-1">
            {/* TODO: data for values? */}
            <DataField
              label="Referral Reason"
              value={"Typical Signs & Symptoms"}
            />
            <DataField
              label="Referral Type"
              value={GetAlertConfirmation(
                signsAndSymptoms?.typicalConfirmationType
              )}
            />
            {(signsAndSymptoms?.typicalConfirmationType === 1 ||
              signsAndSymptoms?.typicalConfirmationType === 2) && (
              <DataField
                label="Explanation"
                value={signsAndSymptoms?.typicalExplaination}
              />
            )}
            <DataField
              label={`Pharmacist Clinical Decision to ${
                signsAndSymptoms?.typicalConfirmationType === 4
                  ? "End Service"
                  : "Continue with Service"
              }`}
              value={""}
            />
          </TealSubSection>
        )}
      </div>
      <Divider />
      <div className="space-y-2">
        <H3>Clinical (Visible) Signs & Symptoms</H3>
        <div className="space-y-4">
          <CheckInput
            checked={
              !!signsAndSymptoms?.visibleSymptoms?.includes?.("allergicShiner")
            }
            label="Allergic Shiners"
          />
          <CheckInput
            checked={
              !!signsAndSymptoms?.visibleSymptoms?.includes?.("allergicSalute")
            }
            label="Allergic Salute"
          />
          <CheckInput
            checked={
              !signsAndSymptoms?.visibleSymptoms?.length ||
              signsAndSymptoms?.visibleSymptoms?.includes?.("none")
            }
            label="None"
          />
        </div>
      </div>
      <Divider />
      <div className="space-y-1">
        <H3>Symptoms correlate with exposure to an allergen(s)</H3>
        {signsAndSymptoms?.allergens?.length ? (
          <P>
            Yes - Allergens:{" "}
            {Array.isArray(signsAndSymptoms?.allergens)
              ? signsAndSymptoms.allergens
                  .map(
                    (a) => allergensEnum.options.find((o) => o.value === a)?.header
                  )
                  .join(", ")
              : ""}
          </P>
        ) : (
          <P>No</P>
        )}
        {signsAndSymptoms?.allergensConfirmationType !== 0 && (
          <TealSubSection className="space-y-[0.0416667in]">
            {/* TODO: data for values? */}
            <DataField
              label="Referral Reason"
              value={"Allergens Alert"}
            />
            <DataField
              label="Referral Type"
              value={GetAlertConfirmation(
                signsAndSymptoms?.allergensConfirmationType
              )}
            />
            {(signsAndSymptoms?.allergensConfirmationType === 1 ||
              signsAndSymptoms?.allergensConfirmationType === 2) && (
              <DataField
                label="Explanation"
                value={signsAndSymptoms?.allergensExplaination}
              />
            )}
            <DataField
              label={`Pharmacist Clinical Decision to ${
                signsAndSymptoms?.allergensConfirmationType === 4
                  ? "End Service"
                  : "Continue with Service"
              }`}
              value={""}
            />
          </TealSubSection>
        )}
      </div>
      <Divider />
      <div className="space-y-2">
        <H3>Red Flag Signs & Symptoms (Non-Typical)</H3>
        <div className="space-y-4">
          <CheckInput
            checked={hasRedFlag}
            label={
              <>
                Red Flag Symptoms
                {redFlagSymptoms ? (
                  <>
                    <br />
                    {redFlagSymptoms}
                  </>
                ) : null}
              </>
            }
          />
          {!hasRedFlag && <CheckInput checked={!hasRedFlag} label="None" />}
        </div>
        {signsAndSymptoms?.nonTypicalConfirmationType !== 0 && (
          <TealSubSection className="space-y-[0.0416667in]">
            {/* TODO: data for values? */}
            <DataField
              label="Referral Reason"
              value={"Red Flag Signs & Symptoms (Non-Typical)"}
            />
            <DataField
              label="Referral Type"
              value={GetAlertConfirmation(
                signsAndSymptoms?.nonTypicalConfirmationType
              )}
            />
            {(signsAndSymptoms?.nonTypicalConfirmationType === 1 ||
              signsAndSymptoms?.nonTypicalConfirmationType === 2) && (
              <DataField
                label="Explanation"
                value={signsAndSymptoms?.nonTypicalExplaination}
              />
            )}
            <DataField
              label={`Pharmacist Clinical Decision to ${
                signsAndSymptoms?.nonTypicalConfirmationType === 4
                  ? "End Service"
                  : "Continue with Service"
              }`}
              value={""}
            />
          </TealSubSection>
        )}
      </div>
    </Section>
  );
};
